import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ogDesign, designJsonLd } from "@/lib/seo";
import { Wand2, ShoppingBag, AlertTriangle, Sparkles } from "lucide-react";

export default function DesignShare() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/studio/public/design/${id}`)
      .then((r) => setD(r.data))
      .catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center px-5 text-center" data-testid="design-share-missing">
        <Seo title="Design Not Found" path={`/d/${id}`} noindex />
        <AlertTriangle className="w-10 h-10 text-amber mb-4" />
        <p className="text-slate-400 font-mono text-sm">This design is private or no longer available.</p>
        <Link to="/studio" className="btn-hud inline-flex mt-6 border border-cyan text-cyan px-6 py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app">
          Design your own
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app relative pt-12 pb-28" data-testid="design-share-page">
      <Seo
        title={d ? `${d.name} — Custom Design` : "Custom Design"}
        description={d
          ? `"${d.name}" — a custom sci-fi design created in the AETHER studio. Print it on premium apparel, or design your own.`
          : "A custom sci-fi design created in the AETHER studio. Print it on premium apparel, or design your own."}
        path={`/d/${id}`}
        image={ogDesign(id)}
        jsonLd={d ? designJsonLd(d, id) : []}
      />
      <div className="absolute inset-0 hud-grid opacity-15" />
      <div className="relative mx-auto max-w-4xl px-5">
        <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.4em] text-cyan/70 mb-6">
          <Sparkles className="w-3.5 h-3.5" /> SHARED FROM THE AETHER STUDIO
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-square border border-cyan/20 brackets overflow-hidden flex items-center justify-center bg-[#0a0f1c]"
            style={{ backgroundImage: "linear-gradient(45deg,#11182a 25%,transparent 25%),linear-gradient(-45deg,#11182a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#11182a 75%),linear-gradient(-45deg,transparent 75%,#11182a 75%)", backgroundSize: "22px 22px", backgroundPosition: "0 0,0 11px,11px -11px,-11px 0px" }}
          >
            {d && <img src={resolveImg(d.image_url)} alt={d.name} className="w-full h-full object-contain p-4" data-testid="design-share-image" />}
          </motion.div>

          <div>
            <h1 className="font-head text-4xl sm:text-5xl font-bold uppercase text-glow-cyan leading-tight" data-testid="design-share-name">
              {d ? d.name : "Loading…"}
            </h1>
            <p className="text-slate-400 mt-5 leading-relaxed">
              A custom sci-fi design forged in the AETHER creative studio. Put it on a premium
              print-on-demand tee or hoodie — or create your own from scratch with AI.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to={d ? `/studio?print=${encodeURIComponent(d.image_url)}&name=${encodeURIComponent(d.name)}` : "/studio"}
                data-testid="design-share-buy"
                className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-7 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app">
                <ShoppingBag className="w-4 h-4" /> Buy this on a tee
              </Link>
              <Link to="/studio" data-testid="design-share-create"
                className="btn-hud flex items-center gap-2 border border-white/20 text-slate-200 px-7 py-3 text-sm hover:border-cyan hover:text-cyan">
                <Wand2 className="w-4 h-4" /> Design your own
              </Link>
              <Link to="/explore" data-testid="design-share-explore"
                className="btn-hud flex items-center gap-2 border border-amber/50 text-amber px-7 py-3 text-sm hover:bg-amber hover:text-app">
                <Sparkles className="w-4 h-4" /> Explore more
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
